import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import DarkNav from '../../components/dark-nav'
import { getCanonicalUrl } from '../../utils/canonical'
import './wordpress.css'

const WordPressIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({ duration: 800, easing: 'ease-out', once: false, offset: 100, mirror: true })
    }
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') AOS.refresh()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const trackEvent = (name, props = {}) => {
    if (typeof gtag !== 'undefined') gtag('event', name, props)
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'WordPress / WooCommerce' }
  ]

  const notifications = [
    { icon: '🛒', title: 'Order Confirmation', desc: 'SMS sent when a new order is placed at checkout.', vars: '{customer_name}, {order_number}, {shop_name}, {total}' },
    { icon: '📦', title: 'Order Shipped', desc: 'SMS sent when order status changes to Completed.', vars: '{customer_name}, {order_number}, {shop_name}' },
    { icon: '❌', title: 'Order Cancelled', desc: 'SMS sent when an order is cancelled.', vars: '{customer_name}, {order_number}, {shop_name}' },
    { icon: '💰', title: 'Refund Processed', desc: 'SMS sent when a full refund is issued.', vars: '{customer_name}, {order_number}, {shop_name}' },
    { icon: '👋', title: 'Welcome (New Customer)', desc: 'SMS sent when a new customer account is created.', vars: '{customer_name}, {shop_name}' },
    { icon: '🛍️', title: 'Abandoned Cart Recovery', desc: 'SMS sent to logged-in customers who leave items in their cart. Configurable delay from 10 minutes to 24 hours.', vars: '{customer_name}, {shop_name}, {cart_url}' },
  ]

  const features = [
    { icon: '🔌', title: 'Simple API Connection', desc: 'Connect using your DataFlows API token. A Test Connection button confirms your account name immediately.' },
    { icon: '📱', title: 'Sender ID Dropdown', desc: 'Sender ID is auto-loaded from your DataFlows account with a Refresh button — no copy-pasting IDs.' },
    { icon: '🌏', title: 'International Numbers', desc: 'Default country code setting normalises local numbers automatically. Strips spaces, dashes, and brackets.' },
    { icon: '⚙️', title: 'Dedicated Admin Panel', desc: 'Dedicated DataFlows SMS menu item in WordPress Admin with separate Settings and Templates pages.' },
    { icon: '🔄', title: 'Smart Number Handling', desc: 'Converts local numbers starting with 0 to international format and adds the + prefix to any number missing it.' },
    { icon: '🎛️', title: 'Per-Notification Toggles', desc: 'Each of the 6 notification types can be enabled or disabled independently from the WordPress admin panel.' },
  ]

  const faqItems = [
    {
      q: 'Does DataFlows SMS work with WooCommerce?',
      a: 'Yes. The DataFlows SMS WordPress plugin integrates directly with WooCommerce order events. It supports Order Confirmation, Shipped, Cancelled, Refund, Welcome, and Abandoned Cart Recovery notifications, all sent automatically when the corresponding WooCommerce order status changes.'
    },
    {
      q: 'How do I connect DataFlows to my WordPress site?',
      a: 'After installing the plugin, go to the DataFlows SMS menu in WordPress Admin, enter your DataFlows API token (found in Developers → Settings in your DataFlows account), and click Test Connection. Your account name will be confirmed immediately.'
    },
    {
      q: 'Can I customise the SMS message for each WooCommerce order status?',
      a: 'Yes. The Templates page in WordPress Admin lets you edit the SMS copy for all 6 notification types separately. Each template supports personalisation variables like {customer_name}, {order_number}, {shop_name}, {total}, and {cart_url}.'
    },
    {
      q: 'Does the plugin support international phone numbers?',
      a: 'Yes. The plugin automatically strips formatting characters (spaces, dashes, brackets) and converts local numbers starting with 0 to international format using your configured country code. You set a default country code (e.g. 61 for Australia) in the Settings page.'
    },
    {
      q: 'Is SMS for WooCommerce compliant with Australian law?',
      a: 'Yes. DataFlows is an ACMA-registered Originating Telco and fully compliant with the Spam Act 2003. All SMS is sent via the DataFlows platform which enforces STOP opt-out handling and tracks opt-out status per subscriber.'
    },
    {
      q: 'How does abandoned cart SMS work in WooCommerce?',
      a: 'The plugin detects when a logged-in customer leaves items in their cart. After a configurable delay (10 minutes to 24 hours), it sends an SMS using your Abandoned Cart template which can include a {cart_url} variable linking the customer back to their cart.'
    },
  ]

  return (
    <div className="wp-container">
      <Helmet>
        <title>WordPress WooCommerce SMS Plugin — Order Notifications & Automation | DataFlows</title>
        <meta name="description" content="Send automated SMS order notifications from WooCommerce. Order confirmation, shipping, abandoned cart recovery, and more. ACMA-compliant Australian SMS plugin for WordPress." />
        <meta name="keywords" content="WooCommerce SMS plugin, WordPress SMS notifications, WooCommerce order SMS, WordPress SMS Australia, WooCommerce abandoned cart SMS, DataFlows WordPress, WooCommerce SMS marketing, WordPress SMS API, WooCommerce SMS automation, Australian WooCommerce SMS" />
        <meta property="og:title" content="WordPress WooCommerce SMS Plugin — Order Notifications | DataFlows" />
        <meta property="og:description" content="Automated SMS order notifications for WooCommerce. Connect DataFlows SMS to WordPress in minutes. ACMA-compliant Australian SMS." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={getCanonicalUrl('/integrations/wordpress')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "DataFlows SMS — WordPress WooCommerce Plugin",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "WordPress",
          "description": "WordPress plugin for sending automated SMS order notifications via WooCommerce, including order confirmation, shipping, and abandoned cart recovery.",
          "offers": { "@type": "Offer", "price": "35.00", "priceCurrency": "AUD" },
          "author": { "@type": "Organization", "name": "DataFlows", "url": "https://dataflows.com.au" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "WordPress / WooCommerce", "item": "https://dataflows.com.au/integrations/wordpress" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      <DarkNav />

      {/* Breadcrumbs */}
      <div className="wp-breadcrumbs">
        <div className="wp-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero */}
      <section className="wp-hero" data-aos="fade-up">
        <div className="wp-inner">
          <div className="wp-hero-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm-.5-13v5.5l4.5 2.7-.75 1.3L10 13.5V7h1.5z"/></svg>
            WordPress / WooCommerce Plugin
          </div>
          <h1 className="wp-hero-title">DataFlows SMS for WordPress &amp; WooCommerce</h1>
          <p className="wp-hero-subtitle">
            Automated SMS order notifications, abandoned cart recovery, and customer welcome messages — all connected to your DataFlows account via a simple WordPress plugin. Built for Australian businesses.
          </p>
          <div className="wp-hero-actions">
            <a
              href="https://sms.dataflows.com.au/register"
              target="_blank"
              rel="noreferrer noopener"
              className="wp-btn-primary"
              onClick={() => trackEvent('wp_get_started_clicked', { source: 'hero' })}
            >
              Get API Key Free
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </a>
            <a href="/contact-us" className="wp-btn-outline">Talk to us</a>
          </div>
          <div className="wp-hero-trust">
            <span>✓ ACMA Registered Originating Telco</span>
            <span>✓ Spam Act 2003 Compliant</span>
            <span>✓ WooCommerce Compatible</span>
          </div>
        </div>
      </section>

      {/* Connection */}
      <section className="wp-section wp-section--dark" id="connection" data-aos="fade-up">
        <div className="wp-inner">
          <div className="wp-two-col">
            <div className="wp-two-col-text">
              <span className="wp-label">Setup</span>
              <h2>Connect in Under 2 Minutes</h2>
              <p>Install the plugin, paste your DataFlows API token, and click Test Connection. Your account name is confirmed immediately — no configuration wizards, no webhooks to set up manually.</p>
              <ul className="wp-checklist">
                <li>API token found in DataFlows → Developers → Settings</li>
                <li>Sender ID auto-loaded with a Refresh button</li>
                <li>Default country code for local number normalisation</li>
                <li>Test Connection button confirms your account instantly</li>
              </ul>
            </div>
            <div className="wp-setup-preview">
              <div className="wp-admin-mockup">
                <div className="wp-admin-row">
                  <span className="wp-admin-label">API Token</span>
                  <div className="wp-admin-input">df_live_••••••••••••••••</div>
                </div>
                <div className="wp-admin-row">
                  <span className="wp-admin-label">Sender ID</span>
                  <div className="wp-admin-select">DataFlows ▾</div>
                </div>
                <div className="wp-admin-row">
                  <span className="wp-admin-label">Country Code</span>
                  <div className="wp-admin-select">61 (Australia) ▾</div>
                </div>
                <div className="wp-admin-test">
                  <span className="wp-admin-btn">✓ Connection Successful</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="wp-section" id="notifications" data-aos="fade-up">
        <div className="wp-inner">
          <div className="wp-section-header">
            <span className="wp-label">Automated</span>
            <h2>6 WooCommerce Order Notifications</h2>
            <p>Each notification type fires automatically on the corresponding WooCommerce order event. Toggle each one on or off from the WordPress admin panel.</p>
          </div>
          <div className="wp-notifications-grid">
            {notifications.map((n, i) => (
              <div key={i} className="wp-notification-card" data-aos="fade-up" data-aos-delay={i * 50}>
                <div className="wp-notification-top">
                  <span className="wp-notification-icon">{n.icon}</span>
                  <h3>{n.title}</h3>
                </div>
                <p className="wp-notification-desc">{n.desc}</p>
                <div className="wp-notification-vars">
                  <span className="wp-vars-label">Variables:</span>
                  <code>{n.vars}</code>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phone Number Handling */}
      <section className="wp-section wp-section--dark" id="phone-handling" data-aos="fade-up">
        <div className="wp-inner">
          <div className="wp-section-header">
            <span className="wp-label">Smart</span>
            <h2>Automatic Phone Number Handling</h2>
            <p>WooCommerce customers enter phone numbers in all kinds of formats. DataFlows SMS normalises every number automatically before sending.</p>
          </div>
          <div className="wp-phone-grid">
            <div className="wp-phone-card">
              <div className="wp-phone-example">
                <span className="wp-phone-in">0412 345 678</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                <span className="wp-phone-out">+61412345678</span>
              </div>
              <p>Local 0-prefix numbers converted to international format using your country code.</p>
            </div>
            <div className="wp-phone-card">
              <div className="wp-phone-example">
                <span className="wp-phone-in">(04) 1234-5678</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                <span className="wp-phone-out">+61412345678</span>
              </div>
              <p>Formatting characters (spaces, dashes, brackets) stripped automatically.</p>
            </div>
            <div className="wp-phone-card">
              <div className="wp-phone-example">
                <span className="wp-phone-in">61412345678</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                <span className="wp-phone-out">+61412345678</span>
              </div>
              <p>+ prefix added to any number missing it.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="wp-section" id="features" data-aos="fade-up">
        <div className="wp-inner">
          <div className="wp-section-header">
            <span className="wp-label">Platform</span>
            <h2>Everything Built Into the Plugin</h2>
          </div>
          <div className="wp-features-grid">
            {features.map((f, i) => (
              <div key={i} className="wp-feature-card" data-aos="fade-up" data-aos-delay={i * 60}>
                <span className="wp-feature-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin Panel */}
      <section className="wp-section wp-section--dark" id="admin" data-aos="fade-up">
        <div className="wp-inner">
          <div className="wp-two-col">
            <div className="wp-two-col-text">
              <span className="wp-label">Admin Panel</span>
              <h2>Manage Everything from WordPress Admin</h2>
              <p>A dedicated DataFlows SMS menu item appears in your WordPress Admin sidebar. Settings and Templates are split into separate pages for clarity.</p>
              <ul className="wp-checklist">
                <li>Settings — API token, country code, sender ID, notification toggles, abandoned cart delay</li>
                <li>Templates — edit SMS copy for all 6 notification types separately</li>
                <li>No separate dashboard needed — manage everything inside WordPress</li>
              </ul>
            </div>
            <div className="wp-admin-sidebar-preview">
              <div className="wp-sidebar">
                <div className="wp-sidebar-item wp-sidebar-item--active">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                  DataFlows SMS
                </div>
                <div className="wp-sidebar-sub">Settings</div>
                <div className="wp-sidebar-sub">Templates</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="wp-section" id="faq" data-aos="fade-up">
        <div className="wp-inner">
          <div className="wp-section-header">
            <span className="wp-label">FAQ</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="wp-faq">
            {faqItems.map((item, i) => (
              <div key={i} className={`wp-faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="wp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <p className={`wp-faq-a${openFaq === i ? '' : ' wp-faq-a--hidden'}`}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="wp-section wp-section--dark" data-aos="fade-up">
        <div className="wp-inner">
          <h2 className="wp-related-title">Related Integrations</h2>
          <div className="wp-related-links">
            <Link to="/integrations/shopify" className="wp-related-link">Shopify SMS App</Link>
            <Link to="/integrations/zapier" className="wp-related-link">Zapier SMS</Link>
            <Link to="/integrations/power-automate" className="wp-related-link">Power Automate SMS</Link>
            <Link to="/integrations/auth0" className="wp-related-link">Auth0 SMS MFA</Link>
            <span className="wp-related-link">WooCommerce SMS Australia</span>
            <span className="wp-related-link">WordPress SMS Notifications</span>
            <span className="wp-related-link">WooCommerce Abandoned Cart SMS</span>
            <span className="wp-related-link">WordPress SMS API</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="wp-cta" data-aos="fade-up">
        <div className="wp-inner">
          <h2>Ready to Add SMS to Your WooCommerce Store?</h2>
          <p>Get your free DataFlows API key, install the plugin, and start sending order SMS in minutes.</p>
          <div className="wp-cta-buttons">
            <a
              href="https://sms.dataflows.com.au/register"
              target="_blank"
              rel="noreferrer noopener"
              className="wp-btn-primary"
              onClick={() => trackEvent('wp_cta_clicked', { source: 'footer-cta' })}
            >
              Get API Key Free
            </a>
            <a href="/contact-us" className="wp-btn-outline">Talk to us</a>
          </div>
        </div>
      </section>

      <Footer />

      {showScrollTop && (
        <div className="scroll-to-top show" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <svg viewBox="0 0 24 24" fill="none"><path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/></svg>
        </div>
      )}
    </div>
  )
}

export default WordPressIntegration
